import { DOCUMENT, isPlatformServer } from '@angular/common';
import { Injectable, NgZone, PLATFORM_ID, inject } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { ISbStory } from '@storyblok/js';
import { BehaviorSubject } from 'rxjs';
import { ScreenSizes } from '../classes/screen-sizes.class';
import { thirdPartyScriptDelay } from '../consts';
import { getAllRouteData } from '../lib/get-all-route-data';
import { getFullPath } from '../lib/get-full-path';
import { loadScript } from '../lib/load-script';
import { parseJwt } from '../lib/parse-jwt';
import { sleep } from '../lib/sleep';
import { AppConfigService } from '../providers/config.service';
import { BrowserService } from './browser.service';
import { FeatureFlagsService } from './feature-flags.service';

@Injectable({
  providedIn: 'root',
})
export class GoogleOneTapService {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private zone = inject(NgZone);
  private document = inject(DOCUMENT);
  private platformId = inject(PLATFORM_ID);
  private featureFlags = inject(FeatureFlagsService);
  private browserService = inject(BrowserService);
  private config = inject(AppConfigService);
  private loadedGoogleOneTap = false;

  public showOneTap$ = new BehaviorSubject<boolean>(false);

  private onShowOneTap$ = this.showOneTap$.subscribe(showOneTap => {
    if (showOneTap) this.loadGoogleOneTap();
  });

  private routeSub$ = this.router.events.subscribe(event => {
    if (event instanceof NavigationEnd) {
      return this.showOneTap$.next(this.canShowGoogleLogin(getAllRouteData(this.route.snapshot).story));
    }
  });

  private canShowGoogleLogin(story: ISbStory['data']['story']): boolean {
    if (isPlatformServer(this.platformId)) return false;
    if (this.browserService.isExtension()) return false;
    if (this.browserService.getCookies()?.['picflow-loggedin'] === 'true') return false;
    const path = getFullPath(this.route.snapshot);
    if (this.featureFlags.get('MAINTENANCE_MODE')) return;
    if (this.route.snapshot.queryParams.noOneTap) return false;
    if (this.config.isTenantDomain) return false;
    if (path[0] === 'gallery') return false; // gallery view on picflow.com
    if (path[0] === 'maintenance') return false;
    if (path[0] === 'auth') return !this.route.snapshot.queryParams.email;
    if (ScreenSizes.screenSize() === 'mobile') return false;
    if (path[0] === 'learn') return false;
    if (path[0] === 'blog') return false;
    if (path[0] === 'press') return false;
    if (story?.content.hideGoogleLogin) return false;
    return true;
  }

  private loadGoogleOneTap() {
    if (this.loadedGoogleOneTap) return;
    if (ScreenSizes.screenSize() === 'mobile') {
      // Not removing the class is on purpose.
      this.document.body.classList.add('show-mobile-google-login');
    }

    this.zone.runOutsideAngular(async () => {
      await sleep(thirdPartyScriptDelay); // Waiting a bit before loading the google one tap client
      this.loadedGoogleOneTap = true;
      loadScript('https://accounts.google.com/gsi/client', { ignoreErrors: true }); // for one tap
      window['googleOneTapPostLogin'] = (e: { credential: string }) => {
        if (!e?.credential) return;
        const content = parseJwt(e.credential);
        if (!content) return;
        this.router.navigate(['/auth', 'login', 'google'], { queryParams: { email: content.email } });
      };
    });
  }
}
